import React from "react";
import { Image, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
    BLACK,
    GOLD,
    GOLD_BORDER_FAINT,
    GRAY,
    GRAY_LIGHT,
    PANEL_BG,
    WHITE,
} from "../../components/PdpAudit/PdpAuditPdf";
import { registerPdfFonts } from "../index";

registerPdfFonts();

export const PDF_FONT = {
    oswaldMedium: "Oswald-Medium",
    oswaldBold: "Oswald-Bold",
    lato: "Lato",
    latoBold: "Lato-Bold",
};

export const TDC_PDF_LOGO =
    typeof window !== "undefined"
        ? `${window.location.origin}/logo-transparent-tdc-only-white.png`
        : "/logo-transparent-tdc-only-white.png";

export const tdcPdfStyles = StyleSheet.create({
    page: {
        flexDirection: "column",
        backgroundColor: BLACK,
        color: WHITE,
        paddingTop: 36,
        paddingHorizontal: 44,
        paddingBottom: 44,
        fontFamily: PDF_FONT.lato,
        fontSize: 10,
    },
    logo: { height: 24, width: 140, objectFit: "contain", marginBottom: 14 },
    headerRule: { height: 3, backgroundColor: GOLD, marginBottom: 20 },
    title: {
        fontFamily: PDF_FONT.oswaldBold,
        fontSize: 24,
        color: WHITE,
        letterSpacing: 2,
        textTransform: "uppercase",
        lineHeight: 1.15,
    },
    subtitle: {
        fontFamily: PDF_FONT.oswaldMedium,
        fontSize: 13,
        color: GOLD,
        marginTop: 4,
    },
    meta: {
        fontFamily: PDF_FONT.lato,
        fontSize: 8,
        color: GRAY,
        marginTop: 6,
        letterSpacing: 1,
    },
    headingRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: PANEL_BG,
        borderWidth: 1,
        borderColor: GOLD_BORDER_FAINT,
        paddingVertical: 6,
        paddingHorizontal: 10,
        marginTop: 22,
        marginBottom: 12,
    },
    headingLabel: {
        fontFamily: PDF_FONT.oswaldMedium,
        fontSize: 10,
        color: GOLD,
        letterSpacing: 2,
        textTransform: "uppercase",
    },
    headingCount: {
        fontFamily: PDF_FONT.lato,
        fontSize: 8,
        color: GRAY,
        letterSpacing: 1,
    },
    entry: { marginBottom: 12 },
    entryLabel: { fontFamily: PDF_FONT.latoBold, fontSize: 9, color: WHITE, lineHeight: 1.3 },
    entryValue: {
        fontFamily: PDF_FONT.lato,
        fontSize: 9.5,
        color: GRAY_LIGHT,
        lineHeight: 1.4,
        marginTop: 3,
    },
    entryMeta: { fontFamily: PDF_FONT.lato, fontSize: 8, color: GRAY },
    empty: { fontFamily: PDF_FONT.lato, fontSize: 10, color: GRAY, marginTop: 20 },
});

export const formatPdfDate = (value?: string): string => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

export const pdfExportedStamp = (prefix: string): string =>
    `${prefix} | EXPORTED ${formatPdfDate(new Date().toISOString()).toUpperCase()}`;

interface HeaderProps {
    title: string;
    subtitle?: string;
    meta?: string;
}

export const TdcPdfHeader: React.FC<HeaderProps> = ({ title, subtitle, meta }) => (
    <>
        <Image style={tdcPdfStyles.logo} src={TDC_PDF_LOGO} />
        <View style={tdcPdfStyles.headerRule} />
        <Text style={tdcPdfStyles.title}>{title}</Text>
        {subtitle ? <Text style={tdcPdfStyles.subtitle}>{subtitle}</Text> : null}
        {meta ? <Text style={tdcPdfStyles.meta}>{meta}</Text> : null}
    </>
);

interface SectionHeadingProps {
    label: string;
    count?: string;
}

export const TdcPdfSectionHeading: React.FC<SectionHeadingProps> = ({ label, count }) => (
    <View style={tdcPdfStyles.headingRow} wrap={false}>
        <Text style={tdcPdfStyles.headingLabel}>{label}</Text>
        {count ? <Text style={tdcPdfStyles.headingCount}>{count}</Text> : null}
    </View>
);

export const pluralCount = (total: number, noun: string): string =>
    `${total} ${total === 1 ? noun.toUpperCase() : `${noun.toUpperCase()}S`}`;

export const downloadPdfBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const pdfFileName = (name: string | undefined, suffix: string, fallback: string): string =>
    `${(name || fallback).replace(/[^a-z0-9]/gi, "_")}_${suffix}.pdf`;
